import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import {
  ORDER_STATE_LABEL,
  ORIGIN_LABEL,
  STAGE_LABEL,
  groupToothItems,
  redoOriginalInSystem,
  sortTeeth,
  caseFinished,
} from '@/domain';
import type { CaseRecord } from '@/application/ports/ArchiveRepository';
import { useKitNotes, useKitWorkProofs } from '@/application/usecases/useKits';
import { useSession } from '@/application/usecases/useSession';
import { strings } from '@/shared/strings';
import { useMoney } from '@/ui/money/moneyView';
import { formatDay, formatInstant } from '@/shared/format/instant';
import { CaseNotes } from '@/ui/data/CaseNotes';
import { CaseInstructions } from '@/ui/data/CaseInstructions';
import { CasePhotos } from '@/features/queue/CasePhotos';
import { ClientLink } from '@/features/clients/ClientLink';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { Odontogram } from '@/ui/data/Odontogram';
import { SpanList } from '@/ui/data/SpanList';
import { ShippingBadge } from '@/ui/data/ShippingBadge';
import { StatusDot } from '@/ui/data/StatusDot';
import { DeletionNote } from '@/features/orders/DeletionControls';
import { ArchiveNote } from './ArchiveNote';
import { EntryProofs, WorkProofStrip } from '@/features/queue/WorkProofStrip';
import { CaseHistory } from './CaseHistory';
import styles from './CaseRecordScreen.module.css';

/**
 * The case record. Section 5.2.
 *
 * A full screen and not a drawer, deliberately: this is the thing somebody
 * opens when a dentist rings up about a case delivered three months ago, and
 * they need the whole history in front of them rather than a panel over the
 * search they will want to go back to.
 */

/**
 * The whole case, as one component. Section 4 of the request of 16 September
 * 2026.
 *
 * The case record and Final Check both show a case, and Final Check showed a
 * hand-built subset: ten fields, the instructions, the notes as bare lines and a
 * compact chart, with no photographs, no history, no times, no proof of work, no
 * bridges, no dates, no doctor or patient and no value. The manager DECIDING
 * whether a case passes was looking at less than the manager who merely opened
 * it from Orders.
 *
 * So there is one component and two call sites: the record route wraps it in a
 * back link and its own actions, and Final Check pins a decision panel under it.
 * The next field added to a case appears in both without anybody remembering to
 * add it twice.
 */
export interface CaseRecordViewProps {
  record: CaseRecord;
  /** Buttons that belong to the route rather than to the case: print, redo, delete. */
  actions?: ReactNode;
  /**
   * Where the record of ANOTHER case opens, for the two links a remake
   * carries: the case it remakes, and the case that remakes it. Null for no
   * link at all, where the reader holds no screen that shows a record.
   *
   * Left off, the view works it out from its own address, which is right on
   * the two routes that are a record. It is wrong anywhere else: drawn over a
   * doctor's or a patient's page it would send everybody to the archive,
   * including somebody who holds Orders and not the archive. A case opened in
   * place says where, by the screens the reader holds. The request of
   * 6 October 2026 (fifth), section 3.
   */
  recordPath?: (jobOrderNo: string) => string | null;
  /**
   * The record is in a narrow box on a screen that may be wide: the drawer a
   * case opens in over a doctor's or a patient's page. Its two leading cards
   * then stand one under the other whatever the width of the window, which is
   * all the stylesheet can otherwise go by.
   */
  narrow?: boolean;
}

export function CaseRecordView({
  record,
  actions,
  recordPath,
  narrow = false,
}: CaseRecordViewProps) {
  const location = useLocation();
  const session = useSession();
  const money = useMoney();
  const workProofs = useKitWorkProofs(record.kits[0]?.id);
  const notes = useKitNotes(record.kits[0]?.id);
  const seesWorkProof = session.data?.permissions.includes('seeWorkProof') === true;
  // A case's own history and log. Section 6 of the request of 20 September 2026.
  const canSeeCaseLog = session.data?.permissions.includes('seeCaseLog') === true;

  const { jobOrder, kits, teeth, history, photos } = record;
  const firstKit = kits[0];
  const product = firstKit?.product ?? '';
  const items = groupToothItems(firstKit?.toothItems ?? []).map((group) => group.product);
  const live = !jobOrder.dispatch.dispatched;
  const fromOrders = location.pathname.startsWith('/orders/');
  const stateLabel = ORDER_STATE_LABEL[jobOrder.state];
  const recordOf =
    recordPath ??
    ((other: string) => (fromOrders ? `/orders/${other}/record` : `/archive/${other}`));
  /** Another case's number: a link to its record where there is a way to it, the number alone where not. */
  const caseLink = (other: string) => {
    const path = recordOf(other);
    return path === null ? other : <Link to={path}>{other}</Link>;
  };
  const deleted = jobOrder.deletion.status === 'deleted';

  return (
    <>
      <div className={styles.head}>
        <span className={styles.lbl}>{live ? strings.record.liveLabel : strings.record.label}</span>
        <h2>{jobOrder.jobOrderNo}</h2>
        <span className={styles.hint}>
          {product} ·{' '}
          {live
            ? `${strings.record.inProduction} · ${strings.record.now} ${
                jobOrder.currentStage === null ? '' : STAGE_LABEL[jobOrder.currentStage]
              }`
            : `${strings.record.dispatchedHint} ${
                jobOrder.dispatch.dispatchedAt === null
                  ? jobOrder.deliveryDate
                  : formatDay(jobOrder.dispatch.dispatchedAt)
              }${
                jobOrder.dispatch.deliveredAt === null
                  ? ''
                  : ` · ${strings.record.receivedHint} ${formatDay(jobOrder.dispatch.deliveredAt)}`
              }`}
        </span>

        {/* Where a remake came from, or where this case went when it came back. */}
        {jobOrder.redoOf !== null && (
          <p className={styles.redo}>
            {/* An original from before this system is a number somebody typed: plain
                text, never a link. The request of 6 October 2026 (second), item 1. */}
            {redoOriginalInSystem(jobOrder) ? (
              <>
                {strings.redo.redoOf} {caseLink(jobOrder.redoOf)}
              </>
            ) : (
              strings.redo.original(jobOrder.redoOf, true)
            )}
            {jobOrder.redoReason !== null && (
              <span className={styles.redoReason}>{jobOrder.redoReason}</span>
            )}
          </p>
        )}
        {jobOrder.redoneAs !== null && (
          <p className={styles.redo}>
            {strings.redo.sentBackAs} {caseLink(jobOrder.redoneAs)}
          </p>
        )}

        {/* A deletion waiting for approval, or the deletion itself, with the decisions on it. */}
        <DeletionNote order={jobOrder} className={styles.deletion} />
        {/* Archived, by whom and why, and the way back. Section 4 of the request of 24 September 2026. */}
        <ArchiveNote order={jobOrder} className={styles.deletion} />
      </div>

      {/*
        What the dentist wants and what the lab wants, in full, directly under
        the head. Section 9 of the request of 20 September 2026 put them here;
        the Doctor, Lab and Notes toggles in front of them went with the request
        of 24 September 2026. Final Check draws this view too, so the decision
        screen shows them in the same place the job view does. The notes are the
        card further down, once.
      */}
      <CaseInstructions text={firstKit?.instructions ?? ''} />

      <div className={narrow ? `${styles.grid} ${styles.stacked}` : styles.grid}>
        <div className={styles.card}>
          <h4>{strings.record.caseDetails}</h4>

          <div className={styles.stat}>
            <span>{strings.record.status}</span>
            {deleted ? (
              <StatusDot tone="hold">{strings.deletion.deleted}</StatusDot>
            ) : live ? (
              <StatusDot tone={stateLabel.tone}>{stateLabel.label}</StatusDot>
            ) : (
              /*
               * Where it stands on its way to the customer, not only that it
               * left, and in the same words the dispatch desk reads. It had its
               * own two tables of labels and tones, which said "In transit"
               * where the shipping screen now says "Shipped" or "Out for
               * delivery". Section 7 of the request of 13 September 2026.
               */
              <ShippingBadge order={jobOrder} />
            )}
          </div>
          {/* Identity is absent for a caller without seeIdentity, so the row is
              omitted rather than showing an empty value. Each name opens its
              profile for a reader who holds that screen, and is the plain name
              for anybody else. The request of 6 October 2026 (second), item 4. */}
          {jobOrder.dentistName != null && (
            <Stat
              label={strings.record.doctor}
              value={
                <ClientLink kind="doctor" id={jobOrder.doctorId} name={jobOrder.dentistName} />
              }
            />
          )}
          {jobOrder.patientName != null && (
            <Stat
              label={strings.record.patient}
              value={
                <ClientLink kind="patient" id={jobOrder.patientId} name={jobOrder.patientName} />
              }
            />
          )}
          {product !== '' && <Stat label={strings.record.product} value={product} />}
          {items.length > 0 && <Stat label={strings.record.items} value={items.join(' · ')} />}
          {firstKit !== undefined && <Stat label={strings.record.shade} value={firstKit.shade} mono />}
          <Stat label={strings.record.units} value={String(jobOrder.kitCount)} mono />
          <Stat label={strings.record.origin} value={ORIGIN_LABEL[jobOrder.origin]} />
          {/* The number on the doctor's paper. The lab's request of 17 October 2026, item 7. */}
          {jobOrder.serialNumber != null && (
            <Stat label={strings.serialNumber.short} value={jobOrder.serialNumber} mono />
          )}
          {live && (
            <Stat
              label={strings.orders.deliveryDate}
              value={
                <DeliveryDate
                  date={jobOrder.deliveryDate}
                  finished={caseFinished(jobOrder)}
                  emphasis
                />
              }
            />
          )}
          {jobOrder.value != null && (
            <Stat label={strings.record.value} value={money(jobOrder.value)} mono />
          )}
          {/*
            How it went. A lab delivery reads as one, with its driver, rather than
            as an empty carrier row, and it has no tracking row because it never
            has a code.
          */}
          {!live && jobOrder.dispatch.method === 'lab' && (
            <Stat
              label={strings.record.carrier}
              value={
                jobOrder.dispatch.driverName === null
                  ? strings.record.labDelivery
                  : `${strings.record.labDelivery} · ${jobOrder.dispatch.driverName}`
              }
            />
          )}
          {!live && jobOrder.dispatch.method !== 'lab' && (
            <>
              <Stat label={strings.record.carrier} value={jobOrder.dispatch.carrier ?? ''} />
              <Stat
                label={strings.record.tracking}
                value={jobOrder.dispatch.trackingCode ?? strings.shipping.noCodeYet}
                mono
              />
            </>
          )}
          {!live && jobOrder.dispatch.dispatchedAt !== null && (
            <Stat
              label={strings.record.dispatchedOn}
              value={formatDay(jobOrder.dispatch.dispatchedAt)}
              mono
            />
          )}
          {!live && (
            <Stat
              label={strings.record.received}
              value={
                jobOrder.dispatch.deliveredAt === null
                  ? strings.record.notReceived
                  : `${formatDay(jobOrder.dispatch.deliveredAt)}${
                      jobOrder.dispatch.receivedBy == null
                        ? ''
                        : ` ${strings.record.receivedBy} ${jobOrder.dispatch.receivedBy}`
                    }`
              }
              mono={jobOrder.dispatch.receivedBy == null}
            />
          )}
          {/*
            Who in the lab marked it received, and when: the moment the mark was
            made, which the day the customer had it need not be. Absent while the
            case is Shipped, and on a case received before the two were kept.
            Lab staff, so every reader of the record sees it. The request of
            5 October 2026 (third), section 11.
          */}
          {!live && jobOrder.dispatch.receivedMarkedAt != null && (
            <Stat
              label={strings.record.markedReceived}
              value={strings.record.markedReceivedBy(
                jobOrder.dispatch.receivedMarkedByUserName ?? null,
                formatInstant(jobOrder.dispatch.receivedMarkedAt),
              )}
            />
          )}

          {/* Print, redo, delete: the route's actions, not the case's. Final
              Check passes none and shows its decision panel instead. */}
          {actions !== undefined && <div className={styles.actions}>{actions}</div>}
        </div>

        <div className={styles.card}>
          <h4>{strings.record.teeth}</h4>
          <Odontogram
            selected={sortTeeth(teeth)}
            readOnly
            size="compact"
            spans={jobOrder.spans}
            showSelection
          />
          <SpanList spans={jobOrder.spans} heading={strings.record.bridges} />
        </div>
      </div>

      <div className={styles.card} style={{ marginTop: 14 }}>
        <h4>
          {strings.record.photos} ({photos.length})
        </h4>
        {/*
          The one strip, which the job view had first: the bytes come through
          the port rather than an authenticated URL on an img, which is what
          made every photograph here a broken image the first time. Section 6
          of the request of 16 September 2026.
        */}
        <CasePhotos photos={photos} jobOrderNo={jobOrder.jobOrderNo} />
      </div>

      {/*
        The proofs of finished work, under the case photographs and never mixed
        in with them, grouped by stage with who took each and when, for a holder
        of seeWorkProof. Section 2.4 of the request of 13 September 2026.
      */}
      {seesWorkProof && (
        <div className={styles.card} style={{ marginTop: 14 }}>
          <h4>
            {strings.proof.cardTitle} ({(workProofs.data ?? []).length})
          </h4>
          {(workProofs.data ?? []).length === 0 ? (
            <p className={styles.none}>{strings.proof.none}</p>
          ) : (
            <WorkProofStrip proofs={workProofs.data ?? []} grouped jobOrderNo={jobOrder.jobOrderNo} />
          )}
        </div>
      )}

      {/*
        Every note on the case, gathered by meaning rather than by where it was
        stored. Read only: a delivered case does not take new notes, which is why
        there is no box under it. Section 3.5 of the request of 16 September 2026.
      */}
      <div className={styles.card} style={{ marginTop: 14 }}>
        <h4 className={styles.cardTitle}>{strings.notes.title}</h4>
        <CaseNotes
          notes={notes.data ?? []}
          // A proof row shows its photographs, to whoever was given them. The
          // request of 5 October 2026 (third), section 6.
          extra={(note) =>
            note.kind === 'proof' && (
              <EntryProofs
                proofs={workProofs.data}
                historyEntryId={note.historyEntryId}
                jobOrderNo={jobOrder.jobOrderNo}
              />
            )
          }
        />
      </div>

      {/*
        One history for the case, where there used to be a stage trail alone:
        the bench entries with their durations, and every action from the job
        order being created to the customer receiving it, merged. The client
        asked for one place, and two lists are not one place.

        ABSENT without `seeCaseLog`, not hidden. Section 6 of the request of
        20 September 2026: the endpoints behind this card refuse without the
        permission, so drawing an empty card would be drawing a card about a
        refusal. Everything else on the record stays.
      */}
      {canSeeCaseLog && (
        <div className={styles.card} style={{ marginTop: 14 }}>
          <CaseHistory
            jobOrderNo={jobOrder.jobOrderNo}
            stages={history}
            proofs={workProofs.data ?? []}
            currentStages={live ? record.kits.map((kit) => kit.stage) : []}
          />
        </div>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className={styles.stat}>
      <span>{label}</span>
      <span className={`${styles.v} ${mono ? styles.mono : ''}`}>{value}</span>
    </div>
  );
}
